import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { Badge, Box, Button, Flex, Icon, IconButton, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaListUl } from "react-icons/fa";
import type { LibraryAwardSubCriterionDto } from "../../../autogen/bff-api";
import { rowOpenProps } from "../../../common/clickableRow";
import { useRequirementDescription } from "../../../common/library/describeRequirement";

// t('No sub-criteria added yet') t('Add reusable requirements your organization can put under a criterion.')
// t('Add sub-criterion') t('Open') t('Delete sub-criterion')

interface Props {
  items: LibraryAwardSubCriterionDto[];
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  readOnly?: boolean;
}

export const AwardSubCriterionList = ({ items, onAdd, onEdit, onDelete, readOnly = false }: Props) => {
  const { t } = useTranslation();
  const describe = useRequirementDescription();

  if (items.length === 0) {
    return (
      <Box
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="lg"
        bg="smBackgroundSecondary"
        py="12"
        px="6"
        textAlign="center"
      >
        <Icon as={FaListUl} boxSize="10" color="smMuted" mb="4" />
        <Text fontWeight="bold" mb="1">
          {t("No sub-criteria added yet")}
        </Text>
        {!readOnly && (
          <>
            <Text color="smMuted" fontSize="sm" mb="6">
              {t("Add reusable requirements your organization can put under a criterion.")}
            </Text>
            <Button colorScheme="teal" leftIcon={<AddIcon />} onClick={onAdd}>
              {t("New sub-criterion")}
            </Button>
          </>
        )}
      </Box>
    );
  }

  // One frame with rows inside it, like the criteria table the wizard uses — not a stack of cards.
  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" overflow="hidden" bg="smBackground">
      {items.map((item, index) => (
        <Flex
          key={`${item.criterionType}-${item.name}-${index}`}
          data-testid="award-sub-criterion-library-row"
          align="center"
          gap="2"
          px="4"
          py="3"
          borderBottomWidth={index === items.length - 1 ? "0" : "1px"}
          borderColor="smBorderSubtle"
          data-group
          _hover={readOnly ? undefined : { bg: "smBackgroundSecondary" }}
          {...(readOnly ? {} : rowOpenProps(() => onEdit(index)))}
        >
          <Box flex="1" minW="0">
            <Flex align="center" gap="2">
              {/* Where it belongs is what makes it findable from inside a criterion. */}
              <Badge
                bg="smBackgroundSecondary"
                color="smMuted"
                px="2"
                py="1"
                borderRadius="md"
                textTransform="none"
                fontSize="2xs"
                flexShrink={0}
              >
                {t(item.criterionType)}
              </Badge>
              <Text fontWeight="bold" fontSize="sm" color="smText" noOfLines={1}>
                {item.name}
              </Text>
            </Flex>
            <Text fontSize="xs" color="smMuted" noOfLines={2}>
              {describe.awardSub(item, { titleShowsType: true })}
            </Text>
          </Box>
          {!readOnly && (
            <Flex align="center" flexShrink={0} whiteSpace="nowrap">
              <IconButton
                aria-label={t("Delete sub-criterion")}
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                onClick={() => onDelete(index)}
              />
            </Flex>
          )}
        </Flex>
      ))}
    </Box>
  );
};
